import React from 'react';
import styled from 'styled-components';
import {
  EventKind,
  FieldContext,
  IBasicEventValueFieldsFragment,
  IFieldFieldsFragment,
  ITagOptionInput,
  TagOption,
} from '../../../../../utils/apollo/graphqlSchema';
import {
  asSingleSelection,
  createBasicEventHandler,
  createTagOptionInput,
} from '../../../EntitySelection/utils';
import EntitySelection from '../../../EntitySelection';
import {
  IRemoveEntityHandler,
  ISelectEntityHandler,
} from '../../../EntitySelection/types';
import { assertCondition } from '../../../../../utils/types';
import { useImmer } from 'use-immer';
import { PrimaryButton } from '../../../../Button';
import Popover from '../../../../Popover';
import { MaybeNil } from '../../../../../utils/interfaces';
import { isNil } from 'lodash';
import { useAppSelector } from '../../../../../redux/hooks';
import { activeSchemaIdSelector } from '../../../../../redux/selectors/builder';

const Container = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export interface IExpandEventValue {
  event: MaybeNil<IBasicEventValueFieldsFragment>;
  field: MaybeNil<IFieldFieldsFragment>;
}

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  anchorEl: React.RefObject<HTMLElement | null>;
  value: IExpandEventValue;
  onChange: (newValue: IExpandEventValue) => void;
}

const expandEventEventTags = [createTagOptionInput(TagOption.BasicEvent)];

const createExpandEventFieldTags = (
  event: MaybeNil<IBasicEventValueFieldsFragment>,
): ITagOptionInput[] => {
  if (isNil(event)) {
    return [];
  }
  return [
    createTagOptionInput(TagOption.Field, {
      field: {
        events: [
          {
            basicEventName: event.value.key,
            kind: EventKind.Basic,
            virtualEventId: null,
            anyTableEventKey: null,
          },
        ],
        func: null,
        context: FieldContext.Breakdown,
        arrayObjectContext: null,
      },
    }),
  ];
};

export default ({
  value: initialValue,
  onChange,
  isOpen,
  onClose,
  anchorEl,
}: IProps) => {
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);

  const [value, setValue] = useImmer<IExpandEventValue>(initialValue);

  const handleSelectEvent: ISelectEntityHandler = (entity) => {
    assertCondition(entity.__typename === 'BasicEventValue');
    setValue((draft) => {
      draft.event = entity;
    });
  };

  const handleRemoveEvent: IRemoveEntityHandler = () => {
    setValue((draft) => {
      draft.event = null;
      draft.field = null;
    });
  };

  const handleSelectField: ISelectEntityHandler = (entity) => {
    assertCondition(entity.__typename === 'Field');
    setValue((draft) => {
      draft.field = entity;
    });
  };

  const handleRemoveField: IRemoveEntityHandler = () => {
    setValue((draft) => {
      draft.field = null;
    });
  };

  const hasEvent = !isNil(value.event);
  const hasField = !isNil(value.field);

  const handleConfirm = () => {
    onChange(value);
  };

  const isFormValid = hasEvent && hasField;

  const fieldTags = createExpandEventFieldTags(value.event);

  return (
    <Popover isOpen={isOpen} close={onClose} anchor={anchorEl.current}>
      <Container>
        <EntitySelection
          placeholder="Select event to expand"
          schemaId={activeSchemaId}
          selectedEntities={asSingleSelection(value.event)}
          tags={expandEventEventTags}
          onSelectEntity={handleSelectEvent}
          onRemoveEntity={handleRemoveEvent}
          onCreateEntity={createBasicEventHandler}
          autoFocus={!hasEvent}
        />
        <EntitySelection
          placeholder="Select field to expand"
          isDisabled={!hasEvent}
          schemaId={activeSchemaId}
          selectedEntities={asSingleSelection(value.field)}
          tags={fieldTags}
          onSelectEntity={handleSelectField}
          onRemoveEntity={handleRemoveField}
        />
        <div>
          <PrimaryButton
            disabled={!isFormValid}
            onClick={handleConfirm}
            trackContext="expand event"
            trackAction="confirm"
          >
            Confirm
          </PrimaryButton>
        </div>
      </Container>
    </Popover>
  );
};
